import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  alpha,
  SxProps,
  Theme,
} from '@mui/material';
import { Search } from '@mui/icons-material';
import { colors } from '../../theme/theme';

/** Shared toggle sizing — matches Studio Payments inner tabs (minHeight 48, ~15px text). */
export const TOGGLE_BUTTON_SIZING: SxProps<Theme> = {
  px: 2.5,
  py: 1.25,
  minHeight: 48,
  fontSize: '0.9375rem',
  fontWeight: 600,
  lineHeight: 1.4,
  textTransform: 'none',
};

export const PAGE_TAB_GROUP_SX: SxProps<Theme> = {
  mb: 3,
  width: '100%',
  flexWrap: 'wrap',
  gap: 1,
  '& .MuiToggleButtonGroup-grouped': {
    ...TOGGLE_BUTTON_SIZING,
    border: `1px solid ${colors.divider}`,
    borderRadius: '10px !important',
    mx: 0,
    color: colors.textSecondary,
    gap: 0.75,
    '&:not(:first-of-type)': {
      marginLeft: '8px',
      borderLeft: `1px solid ${colors.divider}`,
    },
    '&.Mui-selected': {
      color: '#fff',
      fontWeight: 700,
      backgroundColor: colors.primary,
      borderColor: colors.primary,
      '&:hover': {
        backgroundColor: colors.primary,
      },
      '& .MuiSvgIcon-root': {
        color: '#fff',
      },
    },
  },
};

export const FILTER_TAB_GROUP_SX: SxProps<Theme> = {
  flexWrap: 'wrap',
  gap: 1,
  '& .MuiToggleButtonGroup-grouped': {
    ...TOGGLE_BUTTON_SIZING,
    border: `1px solid ${colors.divider}`,
    borderRadius: '10px !important',
    mx: 0,
    color: colors.textSecondary,
    '&:not(:first-of-type)': {
      marginLeft: '8px',
      borderLeft: `1px solid ${colors.divider}`,
    },
    '&.Mui-selected': {
      color: colors.primary,
      fontWeight: 700,
      backgroundColor: `${colors.primary}12`,
      borderColor: alpha(colors.primary, 0.4),
      '&:hover': {
        backgroundColor: `${colors.primary}20`,
      },
    },
  },
};

export interface StatCardProps {
  label: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  color: string;
}

export function StatCard({ label, value, subtitle, icon, color }: StatCardProps) {
  const displayValue = typeof value === 'number' ? value.toLocaleString() : value;

  return (
    <Card
        sx={{
          height: '100%',
          backgroundColor: 'white',
          borderRadius: 3,
          border: `1px solid ${colors.divider}`,
          boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
          transition: 'transform 0.2s, box-shadow 0.2s',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
          },
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={1}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: 2,
                backgroundColor: alpha(color, 0.1),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              {icon}
            </Box>
          </Box>
          <Typography variant="h4" component="h2" fontWeight={700} mb={0.5} color={colors.textPrimary}>
            {displayValue}
          </Typography>
          <Typography variant="body2" color={colors.textSecondary} fontWeight={500}>
            {label}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color={colors.textSecondary} display="block" mt={0.5}>
              {subtitle}
            </Typography>
          )}
        </CardContent>
      </Card>
  );
}

/** Full-width stat card row — matches Reviews page grid pattern. */
export const STAT_CARDS_GRID_SX = {
  display: 'grid',
  gridTemplateColumns: {
    xs: '1fr',
    sm: 'repeat(2, 1fr)',
    lg: 'repeat(auto-fit, minmax(220px, 1fr))',
  },
  gap: 3,
  mb: 4,
  width: '100%',
};

export const DATA_GRID_CONTAINER_SX = {
  height: 650,
  width: '100%',
};

export const DATA_GRID_SX = {
  width: '100%',
  border: 'none',
  '& .MuiDataGrid-cell': {
    fontSize: '0.875rem',
    borderBottom: `1px solid ${alpha(colors.divider, 0.5)}`,
    padding: '16px 20px',
    display: 'flex',
    alignItems: 'center',
  },
  '& .MuiDataGrid-row': {
    transition: 'background-color 0.2s',
    '&:hover': {
      backgroundColor: alpha(colors.primary, 0.05),
    },
    '&:nth-of-type(even)': {
      backgroundColor: alpha(colors.backgroundTertiary, 0.3),
      '&:hover': {
        backgroundColor: alpha(colors.primary, 0.08),
      },
    },
  },
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: alpha(colors.primary, 0.08),
    borderBottom: `2px solid ${colors.primary}`,
    minHeight: '64px !important',
    maxHeight: '64px !important',
    lineHeight: '64px !important',
  },
  '& .MuiDataGrid-columnHeader': {
    padding: '16px 20px',
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    fontWeight: 700,
    fontSize: '0.875rem',
    color: colors.textPrimary,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  '& .MuiDataGrid-footerContainer': {
    borderTop: `1px solid ${alpha(colors.divider, 0.5)}`,
    minHeight: '56px',
  },
  '& .MuiDataGrid-toolbarContainer': {
    padding: '12px 20px',
    borderBottom: `1px solid ${alpha(colors.divider, 0.5)}`,
  },
};

export interface SearchBarProps {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}

export function SearchBar({ value, onChange, placeholder }: SearchBarProps) {
  return (
    <Card
      sx={{
        mb: 3,
        width: '100%',
        borderRadius: 3,
        border: `1px solid ${colors.divider}`,
        boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
      }}
    >
      <CardContent sx={{ py: 2.5 }}>
        <TextField
          fullWidth
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          variant="outlined"
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              backgroundColor: colors.backgroundTertiary,
              '&:hover': {
                backgroundColor: alpha(colors.backgroundTertiary, 0.8),
              },
              '&.Mui-focused': {
                backgroundColor: 'white',
                boxShadow: `0 0 0 3px ${alpha(colors.primary, 0.1)}`,
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: colors.textSecondary }} />
              </InputAdornment>
            ),
          }}
        />
      </CardContent>
    </Card>
  );
}

export interface FilterPillOption<T extends string> {
  value: T;
  label: string;
}

export interface FilterPillsProps<T extends string> {
  options: FilterPillOption<T>[];
  value: T;
  onChange: (value: T) => void;
}

export function FilterPills<T extends string>({ options, value, onChange }: FilterPillsProps<T>) {
  return (
    <ToggleButtonGroup
      exclusive
      value={value}
      onChange={(_, next) => next && onChange(next as T)}
      sx={FILTER_TAB_GROUP_SX}
    >
      {options.map((option) => (
        <ToggleButton key={option.value} value={option.value}>
          {option.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}

const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;

export function isThisMonth(dateStr: string): boolean {
  const nowIST = new Date(Date.now() + IST_OFFSET_MS);
  const dateIST = new Date(new Date(dateStr).getTime() + IST_OFFSET_MS);
  return (
    dateIST.getUTCFullYear() === nowIST.getUTCFullYear() &&
    dateIST.getUTCMonth() === nowIST.getUTCMonth()
  );
}

export async function fetchAllPages<T>(
  fetchPage: (page: number, pageSize: number) => Promise<{ total: number; items: T[] }>,
  pageSize = 1000
): Promise<T[]> {
  const first = await fetchPage(1, pageSize);
  const total = first.total || 0;
  let allItems = [...first.items];
  const totalPages = Math.ceil(total / pageSize);
  if (totalPages > 1) {
    const remaining = await Promise.all(
      Array.from({ length: totalPages - 1 }, (_, i) => fetchPage(i + 2, pageSize))
    );
    for (const page of remaining) {
      allItems = [...allItems, ...page.items];
    }
  }
  return allItems;
}

export function formatSubmittedDate(dateStr: string): { date: string; time: string } {
  const date = new Date(dateStr);
  return {
    date: date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }),
    time: date.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
    }),
  };
}

export const filterCardSx = {
  mb: 3,
  width: '100%',
  borderRadius: 3,
  border: `1px solid ${colors.divider}`,
  boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
};

export const tableCardSx = {
  borderRadius: 3,
  width: '100%',
  border: `1px solid ${colors.divider}`,
  boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
  overflow: 'hidden',
};
